// Consolidation: an institution's projects grouped into at most N larger ones (shared/src/domain/strategy.ts
// stores it as changes, section "consolidation"). Each former project becomes a subproject and keeps its
// code, so its links, decisions and scores still apply. The structure:
//   project      one accountable person, one row in the VULSK cascade
//   subproject   a former project (or part of one, or a new one): its own lead, KPI, activities
//   milestone    a dated result: done or not by that year
//   activity     the work that produces a milestone
// consolidate() proposes one: it groups by what the projects are (their direction, goal or section) and
// how they relate (links between them, shared overlap clusters). It is deterministic: the same content and
// changes give the same proposal. People then move subprojects, rename, add milestones and activities.
// Texts come in English and Lithuanian (`t` and `tLt`, `why` and `whyLt`…): the content has both. A text people
// type in one language is translated into the other (`mt` names the language a machine wrote). With no
// translator the other keeps its text, marked as maybe out of date (`old`), or, new, shows the one typed.
import type { StrategyChange, StrategyState } from './strategy.ts';

export const CONSOLIDATION_YEARS = [2026, 2027, 2028, 2029, 2030] as const;
export const CONSOLIDATION_MAX = 7;

/** The two languages. */
export type ConsolidationLang = 'en' | 'lt';

/** An activity. `p`: proposed (not in the source); `moved`: listed here, done in another subproject (its number). */
export interface Activity {
  t: string;
  tLt?: string;
  /** The language a machine translated it into. */
  mt?: ConsolidationLang;
  /** The language whose text may be out of date: the other was changed and could not be translated. */
  old?: ConsolidationLang;
  p?: boolean;
  moved?: string;
}

/**
 * A milestone in a year; `m` its month (1–12) when the source names one, else the year's end. `sub`: the
 * subproject it belongs to (SubProject.id); `dl` a deadline set outside; `p` proposed.
 */
export interface Milestone {
  y: number;
  m?: number;
  t: string;
  tLt?: string;
  mt?: ConsolidationLang;
  old?: ConsolidationLang;
  sub?: string;
  dl?: boolean;
  p?: boolean;
}

/**
 * A subproject. `ref`: the element it is (a former project), whose title, KPI and targets show unless given
 * here. Without `ref` it is new. Two subprojects may share a `ref`: a project split in two. `acts` replaces
 * the element's own actions when present.
 */
export interface SubProject {
  id: string;
  ref?: string;
  title?: string;
  titleLt?: string;
  kpi?: string;
  kpiLt?: string;
  lead?: string;
  /** The lead's email, when picked from the Users list. */
  leadEmail?: string;
  note?: string;
  noteLt?: string;
  acts?: Activity[];
  /** Fields a machine translated, and into which language; fields whose text in a language may be out of date. */
  mt?: Partial<Record<'title' | 'kpi' | 'note', ConsolidationLang>>;
  old?: Partial<Record<'title' | 'kpi' | 'note', ConsolidationLang>>;
  /** Its period on the Gantt when set by hand: months from January 2026 (0) to December 2030 (59), as the views' Gantt. */
  s?: number;
  e?: number;
  /**
   * The project it is a task of, when people moved it there (plan.ts subGroups' key: `part:…`, `ref:…`, `new:…`), or
   * `own:<its id>`: made a project of its own. Unset: the workbook project it came with, else its element.
   */
  grp?: string;
}

export interface ConsolidatedProject {
  code: string;
  title: string;
  titleLt?: string;
  purpose?: string;
  purposeLt?: string;
  /** Why these belong together. */
  why: string[];
  whyLt?: string[];
  lead?: string;
  /** The lead's email, when picked from the Users list. */
  leadEmail?: string;
  subs: SubProject[];
  milestones: Milestone[];
  notes?: string[];
  notesLt?: string[];
  /** Fields a machine translated, and into which language; fields whose text in a language may be out of date. */
  mt?: Partial<Record<'title' | 'purpose', ConsolidationLang>>;
  old?: Partial<Record<'title' | 'purpose', ConsolidationLang>>;
}

export interface ConsolidationMeta {
  inst: string;
  /** The projects' codes, in order. */
  order: string[];
  max: number;
  /** auto: made by consolidate(); curated: loaded from a reviewed file. */
  method: 'auto' | 'curated';
  at: string;
  by?: string;
  note?: string;
}

export interface Consolidation {
  meta: ConsolidationMeta;
  projects: ConsolidatedProject[];
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : []);
const s = (v: unknown): string => (v == null ? '' : String(v));
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.map(s) : []);
const en = (x: Obj, k: string): unknown => (isObj(x.en) ? x.en[k] : undefined);
/** A project as read from a file or the changes: `why` and `milestones` lists even when missing. Valid ones come back as they are. */
const asProject = (p: Obj): ConsolidatedProject =>
  (Array.isArray(p.why) && Array.isArray(p.milestones) ? p : { ...p, why: Array.isArray(p.why) ? p.why : [], milestones: Array.isArray(p.milestones) ? p.milestones : [] }) as unknown as ConsolidatedProject;
/** An outline as read: `at` a text even when missing. */
const asMeta = (m: Obj): ConsolidationMeta => (typeof m.at === 'string' ? m : { ...m, at: '' }) as unknown as ConsolidationMeta;

/** The levels that are no institution of their own: their elements are rules and programmes, not projects. */
const NOT_INSTITUTIONS = new Set(['NAT', 'EU']);
/** As the views name them (viewer.html INST0). */
const NAMES: Record<string, string> = { VPC: 'VPC (NCP)', LMC: 'LMC (Lab Medicine)', VULSK: 'VULSK', 'VU MF': 'VU MF', CDM: 'CDM' };
/** Element kinds that are work to do (a project, a programme, a task, a measure); directions and goals group them. */
const WORK_KINDS = new Set(['project', 'programme', 'task', 'measure']);

/** An element of an institution's work, as consolidate() reads it: with the edits people made. */
export interface PlanElement {
  id: string;
  inst: string;
  title: string;
  titleLt: string;
  /** The immediate parent (a goal, a direction, a section) and the top one; '' when none. */
  parent: string;
  group: string;
  /** Yearly targets, 2026 first; English when the content has it. `…Lt`: the Lithuanian. */
  targets: string[];
  actions: string[];
  kpi: string;
  targetsLt: string[];
  actionsLt: string[];
  kpiLt: string;
  deadline: string;
  mandate: number;
  dependants: string[];
  /** Its place in the content: keeps the source's order. */
  order: number;
}

/** An edited text (the views keep edits under txt.en / txt.lt), else the content's. */
function edited(edit: Obj | undefined, field: string, lang: 'en' | 'lt', fallback: string): string {
  const txt = isObj(edit?.txt) ? edit.txt : {};
  const v = isObj(txt[lang]) ? txt[lang][field] : undefined;
  return typeof v === 'string' && v.trim() ? v : fallback;
}

/** The institutions that have work to consolidate (two elements or more), with how many. */
export function institutionsOf(content: Obj, state: StrategyState): { id: string; name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const e of allElements(content, state)) counts.set(e.inst, (counts.get(e.inst) ?? 0) + 1);
  const custom = new Map(Object.values(state.institutions).filter(isObj).map((i) => [s(i.id), s(i.en) || s(i.lt) || s(i.id)]));
  return [...counts]
    .filter(([id, n]) => n >= 2 && !NOT_INSTITUTIONS.has(id))
    .map(([id, count]) => ({ id, name: NAMES[id] ?? custom.get(id) ?? id, count }));
}

/** Every work element of every institution, with people's edits; deleted ones left out. */
function allElements(content: Obj, state: StrategyState): PlanElement[] {
  const edits = state.edits as Record<string, Obj | undefined>;
  const external = list(content.external);
  const byId = new Map(external.map((x) => [s(x.id), x]));
  const inContent = new Set([...list(content.projects), ...external].map((x) => s(x.id)));
  // A measure numbered 2.1.1 sits under 2.1 of the same institution, when the content has no parent.
  const codeParent = (x: Obj): string => {
    const code = s(x.code);
    if (!code.includes('.')) return '';
    const up = code.slice(0, code.lastIndexOf('.'));
    return external.find((y) => y.source === x.source && s(y.code) === up)?.id as string ?? '';
  };
  const parentOf = (x: Obj): string => s(x.parent) || codeParent(x);
  const isParent = new Set(external.map(parentOf).filter(Boolean));
  // The top of the chain. Numbered measures (1.1, 2.1.3) with no element above them belong to their
  // section, the first number: a group of its own, named after its members (the content has no title for it).
  const topOf = (id: string): string => {
    let cur = id;
    for (let i = 0; i < 10; i++) {
      const x = byId.get(cur);
      const up = x ? parentOf(x) : '';
      if (!up) {
        const code = x ? s(x.code) : '';
        return code.includes('.') ? `${s(x!.source)}§${code.split('.')[0]}` : cur;
      }
      cur = up;
    }
    return cur;
  };
  const out: PlanElement[] = [];
  let order = 0;
  const base = (id: string, inst: string, x: Obj): Omit<PlanElement, 'parent' | 'group'> => {
    const ed = edits[id];
    return {
      id,
      inst,
      title: edited(ed, 'title', 'en', s(en(x, 'title')) || s(x.title)),
      titleLt: edited(ed, 'title', 'lt', s(x.title)),
      targets: strs(en(x, 'targets') ?? x.targets),
      actions: strs(en(x, 'actions') ?? x.actions),
      kpi: s(en(x, 'kpi')) || s(x.kpi),
      targetsLt: strs(x.targets),
      actionsLt: strs(x.actions),
      kpiLt: s(x.kpi),
      deadline: s(x.deadline),
      mandate: Number(x.mandate) || 0,
      dependants: strs(x.dependants),
      order: order++,
    };
  };
  for (const p of list(content.projects)) {
    const id = s(p.id);
    if (edits[id]?.deleted) continue;
    const dir = s(edits[id]?.dir) || s(p.dir);
    out.push({ ...base(id, s(p.unit) || 'VPC', p), parent: dir, group: dir });
  }
  for (const x of external) {
    const id = s(x.id);
    if (!WORK_KINDS.has(s(x.kind)) || isParent.has(id) || edits[id]?.deleted) continue;
    const parent = parentOf(x);
    out.push({ ...base(id, s(x.source), x), parent, group: topOf(parent || id) });
  }
  // A new element whose id the content now has: the content's element only, as in the views.
  for (const [id, ed] of Object.entries(edits)) {
    if (!isObj(ed) || !ed.isNew || ed.deleted || !s(ed.inst) || inContent.has(id)) continue;
    const dir = s(ed.dir);
    out.push({ ...base(id, s(ed.inst), {}), parent: dir, group: dir });
  }
  return out;
}

/** The institution's work elements, in the content's order. */
export function elementsOf(content: Obj, state: StrategyState, inst: string): PlanElement[] {
  return allElements(content, state).filter((e) => e.inst === inst);
}

/** A link, as the views draw it. */
export interface PlanLink {
  id: string;
  from: string;
  to: string;
  type: string;
  strength: number;
}

/** The content's links and people's own, without the rejected ones. */
export function linksOf(content: Obj, state: StrategyState): PlanLink[] {
  const all = [...list(content.relations), ...Object.values(state.links).filter(isObj)];
  return all
    .map((r) => ({ id: s(r.id), from: s(r.from_ ?? r.from), to: s(r.to), type: s(r.type), strength: Number(r.strength) || 1 }))
    .filter((l) => l.from && l.to && (state.decisions[l.id] as Obj | undefined)?.s !== 'rejected');
}

/** The title of a direction, goal or section, English first. A numbered section ("VU MF§2") has none in the content: "Section 2". */
function titleOf(content: Obj, id: string): { en: string; lt: string } {
  const sec = /§(.+)$/.exec(id);
  if (sec) return { en: `Section ${sec[1]}`, lt: `${sec[1]} skyrius` };
  const x = [...list(content.directions), ...list(content.external)].find((y) => y.id === id);
  return x ? { en: s(en(x, 'title')) || s(x.title), lt: s(x.title) } : { en: '', lt: '' };
}

/** What a parent is called: a direction, a goal, a section (Lithuanian: as the object of "turi bendrą …"). */
function kindOf(content: Obj, id: string): { en: string; lt: string } {
  if (id.includes('§')) return { en: 'a section', lt: 'skyrių' };
  if (list(content.directions).some((d) => d.id === id)) return { en: 'a direction', lt: 'kryptį' };
  const k = s(list(content.external).find((x) => x.id === id)?.kind);
  if (k === 'goal') return { en: 'a goal', lt: 'tikslą' };
  if (k === 'direction') return { en: 'a direction', lt: 'kryptį' };
  return { en: 'a parent', lt: 'aukštesnį elementą' };
}

/** A link's verb, in both languages. */
const VERB: Record<string, { en: string; lt: string }> = {
  depends_on: { en: 'depends on', lt: 'priklauso nuo' },
  enables: { en: 'enables', lt: 'įgalina' },
  supports: { en: 'supports', lt: 'palaiko' },
  requires: { en: 'requires', lt: 'reikalauja' },
  conflicts: { en: 'conflicts with', lt: 'prieštarauja' },
};
const verb = (type: string) => VERB[type] ?? { en: type.replace('_', ' '), lt: type.replace('_', ' ') };

/** Where a group comes in the source: its direction's or element's place; members' places otherwise. */
function rankOf(content: Obj, id: string): number {
  const d = list(content.directions).findIndex((x) => x.id === id);
  if (d >= 0) return d;
  const x = list(content.external).findIndex((y) => y.id === id);
  return x >= 0 ? 1000 + x : -1;
}

/** How strongly two elements belong together. */
const SAME_PARENT = 2;
const SAME_GROUP = 1;
const LINK_ORDER = 1.5; // depends on / enables, per point of strength
const LINK_OTHER = 0.75;
const SAME_CLUSTER = 2;
const SHARED_TARGET = 0.5;
/** Below this average, groups are not merged once there are few enough. */
const MERGE_MIN = 1;

/** Prerequisite first: "a depends on b" and "b enables a" both put b before a. */
function before(l: PlanLink): [string, string] | null {
  if (l.type === 'depends_on') return [l.to, l.from];
  if (l.type === 'enables') return [l.from, l.to];
  return null;
}

/** Elements in dependency order (prerequisites first), the content's order breaking ties. */
function ordered(els: PlanElement[], links: PlanLink[]): PlanElement[] {
  const ids = new Set(els.map((e) => e.id));
  const after = new Map<string, Set<string>>(els.map((e) => [e.id, new Set()]));
  const need = new Map<string, number>(els.map((e) => [e.id, 0]));
  for (const l of links) {
    const ab = before(l);
    if (!ab || !ids.has(ab[0]) || !ids.has(ab[1]) || ab[0] === ab[1] || after.get(ab[0])!.has(ab[1])) continue;
    after.get(ab[0])!.add(ab[1]);
    need.set(ab[1], need.get(ab[1])! + 1);
  }
  const out: PlanElement[] = [];
  const left = [...els].sort((a, b) => a.order - b.order);
  while (left.length) {
    // A loop of dependencies cannot be ordered: the first left goes, in the content's order.
    const i = Math.max(0, left.findIndex((e) => need.get(e.id) === 0));
    const [e] = left.splice(i, 1);
    out.push(e!);
    for (const n of after.get(e!.id)!) need.set(n, need.get(n)! - 1);
  }
  return out;
}

/** Lithuanian month names, by stem (nominative or genitive: "spalis", "spalio"). Rugpj before rugs; vasario, not vasaros (summer). */
const LT_MONTHS: [RegExp, number][] = [
  [/\bsaus/i, 1], [/\bvasar[iįy]/i, 2], [/\bkov/i, 3], [/\bbaland/i, 4], [/\bgegu/i, 5], [/\bbirž/i, 6],
  [/\bliep/i, 7], [/\brugpj/i, 8], [/\brugs/i, 9], [/\bspal/i, 10], [/\blapkr/i, 11], [/\bgruod/i, 12],
];
const QUARTER_END: Record<string, number> = { I: 3, II: 6, III: 9, IV: 12 };

/** The month a text names: a date (2026-12-15, 2026-12), a Lithuanian month name, a quarter ("III ketv.", "III–IV ketv.": the last). */
export function monthIn(text: string): number | undefined {
  const d = /\b20\d\d-(0[1-9]|1[0-2])\b/.exec(text);
  if (d) return Number(d[1]);
  const q = /\b(I{1,3}|IV)(?:\s*[–-]\s*(I{1,3}|IV))?\s*ketv/.exec(text);
  if (q) return QUARTER_END[q[2] ?? q[1]!];
  for (const [re, m] of LT_MONTHS) if (re.test(text)) return m;
  return undefined;
}

/** "Maintain" is no milestone: nothing new is done that year. */
const MAINTAIN = /^(maintain|išlaikyti|-|–|—)?$/i;
const YEAR = /\b(20[2-3]\d)\b/g;

/** A year the plan covers (2026–2030). */
const inPlanYears = (y: number): boolean => y >= CONSOLIDATION_YEARS[0] && y <= CONSOLIDATION_YEARS[CONSOLIDATION_YEARS.length - 1]!;

/** Milestones of a deadline text: each part ("modelis 2027 m. III–IV ketv.; prieigos tvarka 2028 m.") with one year. A span of years ("2026–2027", "2026-27") is no milestone. */
export function deadlineMilestones(text: string, sub: string, dl: boolean): Milestone[] {
  const out: Milestone[] = [];
  for (const part of text.split(';').map((x) => x.trim()).filter(Boolean)) {
    const years = [...part.matchAll(YEAR)].map((m) => Number(m[1]));
    // "2026-27": the second year's last digits, above the first's ("2026-12" is a month).
    const short = /\b20(\d\d)\s*[–-]\s*(\d\d)\b/.exec(part);
    if (years.length !== 1 || /[–-]\s*20\d\d/.test(part) || (short && Number(short[2]) > Number(short[1]))) continue;
    const y = years[0]!;
    if (!inPlanYears(y)) continue;
    const m = monthIn(part);
    out.push({ y, ...(m ? { m } : {}), t: part, tLt: part, sub, ...(dl || /\biki\b|\d{4}-\d{2}/.test(part) ? { dl: true } : {}) });
  }
  return out;
}

/** A parent goal's target, as the milestone its work leads to at the strategy's end (2030). */
function goalMilestone(content: Obj, parent: string, sub: string): Milestone | null {
  const g = list(content.external).find((x) => x.id === parent);
  const target = g ? s(en(g, 'target')) || s(g.target) : '';
  if (!g || !target) return null;
  const kpi = s(en(g, 'kpi')) || s(g.kpi);
  const kpiLt = s(g.kpi);
  const targetLt = s(g.target) || target;
  return {
    y: CONSOLIDATION_YEARS[CONSOLIDATION_YEARS.length - 1]!,
    t: `Goal ${s(g.code)}: ${kpi ? `${kpi} ` : ''}${target}`,
    tLt: `Tikslas ${s(g.code)}: ${kpiLt ? `${kpiLt} ` : ''}${targetLt}`,
    sub,
  };
}

/** An element's milestones: its yearly targets, dated actions, and its deadline. */
function milestonesOf(e: PlanElement, sub: string): Milestone[] {
  const out: Milestone[] = [];
  e.targets.slice(0, CONSOLIDATION_YEARS.length).forEach((t, i) => {
    const lt = e.targetsLt[i] || t;
    if (!MAINTAIN.test(t.trim()) && !MAINTAIN.test(lt.trim())) out.push({ y: CONSOLIDATION_YEARS[i]!, t, ...(lt !== t ? { tLt: lt } : {}), sub });
  });
  for (let i = 0; i < Math.max(e.actions.length, e.actionsLt.length); i++) {
    const a = e.actions[i] ?? e.actionsLt[i]!;
    const lt = e.actionsLt[i] ?? a;
    const d = /\b(20[2-3]\d)-(\d{2})-\d{2}\b/.exec(a) ?? /\b(20[2-3]\d)-(\d{2})-\d{2}\b/.exec(lt);
    const y = Number(d?.[1]);
    if (d && inPlanYears(y)) out.push({ y, m: Number(d[2]), t: a, ...(lt !== a ? { tLt: lt } : {}), sub, dl: true });
  }
  if (e.deadline) out.push(...deadlineMilestones(e.deadline, sub, false));
  return out;
}

/**
 * A proposal for `inst`: at most `max` projects. Elements are merged by average affinity (same parent,
 * same group, links between them, shared overlap clusters, shared outside targets) while there are more
 * than `max` groups, and after that only while they clearly belong together.
 */
export function consolidate(content: Obj, state: StrategyState, inst: string, max = CONSOLIDATION_MAX, now = new Date()): Consolidation {
  const els = elementsOf(content, state, inst);
  const links = linksOf(content, state);
  const ids = new Set(els.map((e) => e.id));
  const idx = new Map(els.map((e, i) => [e.id, i]));
  const n = els.length;
  const aff: number[][] = Array.from({ length: n }, () => Array<number>(n).fill(0));
  const add = (a: number, b: number, w: number) => {
    if (a === b) return;
    aff[a]![b]! += w;
    aff[b]![a]! += w;
  };
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      if (els[i]!.parent && els[i]!.parent === els[j]!.parent) add(i, j, SAME_PARENT);
      if (els[i]!.group && els[i]!.group === els[j]!.group) add(i, j, SAME_GROUP);
    }
  const outside = new Map<number, Set<string>>();
  for (const l of links) {
    const a = idx.get(l.from);
    const b = idx.get(l.to);
    if (a !== undefined && b !== undefined) add(a, b, (before(l) ? LINK_ORDER : LINK_OTHER) * l.strength);
    else if (a !== undefined) (outside.get(a) ?? outside.set(a, new Set()).get(a)!).add(l.to);
    else if (b !== undefined) (outside.get(b) ?? outside.set(b, new Set()).get(b)!).add(l.from);
  }
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const a = outside.get(i);
      const b = outside.get(j);
      if (a && b) add(i, j, Math.min(2, [...a].filter((t) => b.has(t)).length * SHARED_TARGET));
    }
  const clusters = list(content.overlaps).map((o) => ({
    id: s(o.id),
    label: s(en(o, 'label')) || s(o.label),
    labelLt: s(o.label) || s(en(o, 'label')),
    members: strs(o.members).filter((m) => ids.has(m)),
  }));
  for (const o of clusters) for (const a of o.members) for (const b of o.members) if (a < b) add(idx.get(a)!, idx.get(b)!, SAME_CLUSTER);

  let groups = els.map((_, i) => [i]);
  const avg = (A: number[], B: number[]) => A.reduce((t, a) => t + B.reduce((u, b) => u + aff[a]![b]!, 0), 0) / (A.length * B.length);
  while (groups.length > 1) {
    let best = -1;
    let pair: [number, number] = [0, 1];
    for (let i = 0; i < groups.length; i++)
      for (let j = i + 1; j < groups.length; j++) {
        const v = avg(groups[i]!, groups[j]!);
        const size = groups[i]!.length + groups[j]!.length;
        const bestSize = groups[pair[0]]!.length + groups[pair[1]]!.length;
        if (v > best || (v === best && size < bestSize)) [best, pair] = [v, [i, j]];
      }
    if (groups.length <= max && best < MERGE_MIN) break;
    const [i, j] = pair;
    groups[i] = [...groups[i]!, ...groups[j]!];
    groups.splice(j, 1);
  }
  const firstPlace = (g: number[]) => Math.min(...g.map((a) => els[a]!.order));
  const groupPlace = (g: number[]) => {
    const n = new Map<string, number>();
    for (const a of g) if (els[a]!.group) n.set(els[a]!.group, (n.get(els[a]!.group) ?? 0) + 1);
    const top = [...n].sort((x, y) => y[1] - x[1])[0]?.[0];
    const r = top ? rankOf(content, top) : -1;
    return r >= 0 ? r : 100_000 + firstPlace(g);
  };
  groups.sort((A, B) => groupPlace(A) - groupPlace(B) || firstPlace(A) - firstPlace(B));

  const projects = groups.map((g, gi): ConsolidatedProject => {
    const members = ordered(g.map((i) => els[i]!), links);
    const mid = new Set(members.map((m) => m.id));
    const subs: SubProject[] = members.map((m) => ({ id: `s-${m.id}`, ref: m.id }));
    // Named after what most members share: a parent, else a group (more than half of them); else the member most linked inside.
    const most = (k: 'parent' | 'group'): { id: string; all: boolean } => {
      const n = new Map<string, number>();
      for (const m of members) if (m[k]) n.set(m[k], (n.get(m[k]) ?? 0) + 1);
      const [id = '', c = 0] = [...n].sort((a, b) => b[1] - a[1])[0] ?? [];
      return c * 2 > members.length && titleOf(content, id).en ? { id, all: c === members.length } : { id: '', all: false };
    };
    const degree = (m: PlanElement) => links.filter((l) => (l.from === m.id && mid.has(l.to)) || (l.to === m.id && mid.has(l.from))).length;
    const anchor = [...members].sort((a, b) => degree(b) - degree(a) || a.order - b.order)[0]!;
    // One subproject: the project is simply it.
    const byParent = members.length > 1 ? most('parent') : { id: '', all: false };
    const shared = byParent.id || members.length === 1 ? byParent : most('group');
    const name = shared.id ? titleOf(content, shared.id) : { en: '', lt: '' };
    const why: string[] = [];
    const whyLt: string[] = [];
    const say2 = (e: string, l: string) => {
      why.push(e);
      whyLt.push(l);
    };
    if (shared.id) {
      const k = kindOf(content, shared.id);
      say2(`${shared.all ? 'They share' : 'Most of them share'} ${k.en}: ${name.en}.`, `${shared.all ? 'Jie turi bendrą' : 'Dauguma jų turi bendrą'} ${k.lt}: ${name.lt || name.en}.`);
    }
    const inside = links.filter((l) => mid.has(l.from) && mid.has(l.to) && l.from !== l.to);
    if (inside.length) {
      const some = inside.slice(0, 4);
      const more = inside.length - some.length;
      say2(
        `Linked to each other: ${some.map((l) => `${l.from} ${verb(l.type).en} ${l.to} (${l.strength})`).join('; ')}${more > 0 ? `; ${more} more` : ''}.`,
        `Susiję tarpusavyje: ${some.map((l) => `${l.from} ${verb(l.type).lt} ${l.to} (${l.strength})`).join('; ')}${more > 0 ? `; dar ${more}` : ''}.`,
      );
    }
    for (const o of clusters) {
      const inHere = o.members.filter((m) => mid.has(m));
      if (inHere.length >= 2)
        say2(`The same work in overlap cluster ${o.id} ${o.label}: ${inHere.join(', ')}.`, `Tas pats darbas persidengimo klasteryje ${o.id} ${o.labelLt}: ${inHere.join(', ')}.`);
    }
    for (const m of members) if (m.mandate >= 3) say2(`${m.id} carries a full-strength mandate: an obligation.`, `${m.id} turi visos galios mandatą: tai įsipareigojimas.`);
    for (const m of members)
      if (m.dependants.length >= 2) say2(`${m.id} is a gate: ${m.dependants.length} projects wait on it.`, `${m.id} – vartų projektas: nuo jo priklausančių projektų: ${m.dependants.length}.`);
    if (!why.length) say2('Grouped with the projects closest to it; nothing links it more strongly elsewhere.', 'Sugrupuota su artimiausiais projektais; niekas jo stipriau nesieja su kitais.');
    // One milestone per parent goal with a target, not one per programme under it.
    const goals = new Map<string, Milestone>();
    for (const m of members) {
      const g = m.parent && !goals.has(m.parent) ? goalMilestone(content, m.parent, `s-${m.id}`) : null;
      if (g) goals.set(m.parent, g);
    }
    const milestones = [...members.flatMap((m) => milestonesOf(m, `s-${m.id}`)), ...goals.values()]
      .sort((a, b) => a.y - b.y || subs.findIndex((x) => x.id === a.sub) - subs.findIndex((x) => x.id === b.sub));
    return {
      code: `P${gi + 1}`,
      title: name.en || anchor.title,
      titleLt: name.lt || anchor.titleLt,
      why,
      whyLt,
      subs,
      milestones,
    };
  });
  return { meta: { inst, order: projects.map((p) => p.code), max, method: 'auto', at: now.toISOString() }, projects };
}

/** The keys a consolidation is kept under, in the changes' section "consolidation". */
export const consolidationKey = (inst: string, code?: string): string => (code ? `${inst}|${code}` : inst);

/** The consolidation saved for `inst`, or null. Projects in their order; one whose row is missing is left out. */
export function savedConsolidation(state: StrategyState, inst: string): Consolidation | null {
  const meta = state.consolidation[consolidationKey(inst)];
  if (!isObj(meta) || !Array.isArray(meta.order)) return null;
  const projects = (meta.order as unknown[])
    .map((code) => state.consolidation[consolidationKey(inst, s(code))])
    .filter((p): p is Obj => isObj(p) && Array.isArray(p.subs))
    .map(asProject);
  return { meta: asMeta(meta), projects };
}

/** The changes that store `next` for `inst` in place of what `state` has: changed keys set, keys it no longer has removed. */
export function consolidationChanges(state: StrategyState, inst: string, next: Consolidation | null): StrategyChange[] {
  const mine = Object.keys(state.consolidation).filter((k) => k === inst || k.startsWith(`${inst}|`));
  const want = new Map<string, unknown>();
  if (next) {
    want.set(consolidationKey(inst), { ...next.meta, order: next.projects.map((p) => p.code) });
    for (const p of next.projects) want.set(consolidationKey(inst, p.code), p);
  }
  const out: StrategyChange[] = [];
  for (const [key, value] of want) {
    if (JSON.stringify(state.consolidation[key]) !== JSON.stringify(value)) out.push({ section: 'consolidation', key, value });
  }
  for (const key of mine) if (!want.has(key)) out.push({ section: 'consolidation', key, value: null });
  return out;
}

/**
 * Directions (consolidated projects) these changes add or remove, against `saved` (the section "consolidation"): a new
 * consolidation, one removed, a project key set that was not there or removed that was, an outline whose projects
 * differ. Only an administrator may (http/strategy.ts); '' when they do none of it.
 */
export function directionChangesProblem(changes: readonly StrategyChange[], saved: Record<string, unknown>): string {
  const codes = (v: unknown) => (isObj(v) && Array.isArray(v.order) ? [...new Set(v.order.map(s))].sort().join('|') : null);
  for (const c of changes) {
    if (c.section !== 'consolidation') continue;
    const had = Object.prototype.hasOwnProperty.call(saved, c.key);
    const changed = c.key.includes('|') ? had !== (c.value !== null) : c.value === null ? had : !had || codes(saved[c.key]) !== codes(c.value);
    if (changed) return 'Only an administrator can add or delete a Direction.';
  }
  return '';
}

/** The links between projects: what each needs from another, and gives. "a depends on b": a needs b. */
export function projectLinks(c: Consolidation, links: PlanLink[]): Map<string, { needs: { code: string; text: string }[]; gives: { code: string; text: string }[] }> {
  const home = new Map<string, string>();
  for (const p of c.projects) for (const sub of p.subs) if (sub.ref) home.set(sub.ref, p.code);
  const out = new Map(c.projects.map((p) => [p.code, { needs: [] as { code: string; text: string }[], gives: [] as { code: string; text: string }[] }]));
  for (const l of links) {
    const ab = before(l);
    if (!ab) continue;
    const [first, then] = ab;
    const a = home.get(first);
    const b = home.get(then);
    if (!a || !b || a === b) continue;
    const text = l.type === 'depends_on' ? `${l.from} depends on ${l.to}` : `${l.from} enables ${l.to}`;
    out.get(b)!.needs.push({ code: a, text });
    out.get(a)!.gives.push({ code: b, text });
  }
  return out;
}

/** A project's kinds: an obligation (a subproject with a full-strength mandate), a gate (one that two or more wait on). */
export function projectFlags(p: ConsolidatedProject, byId: Map<string, PlanElement>): ('obligation' | 'gate')[] {
  const els = p.subs.map((x) => (x.ref ? byId.get(x.ref) : undefined)).filter((e): e is PlanElement => !!e);
  return [...(els.some((e) => e.mandate >= 3) ? (['obligation'] as const) : []), ...(els.some((e) => e.dependants.length >= 2) ? (['gate'] as const) : [])];
}

/** A file of a consolidation (the page's Download, a reviewed proposal): {consolidation: {meta, projects}}. */
export function consolidationFile(v: unknown): Consolidation | null {
  const c = isObj(v) && isObj(v.consolidation) ? v.consolidation : null;
  if (!c || !isObj(c.meta) || !s(c.meta.inst) || !Array.isArray(c.projects)) return null;
  const projects = list(c.projects).filter((p) => s(p.code) && Array.isArray(p.subs)).map(asProject);
  if (!projects.length || new Set(projects.map((p) => p.code)).size !== projects.length) return null;
  return { meta: asMeta(c.meta), projects };
}

// ── the Gantt ───────────────────────────────────────────────────────────────

/** January 2026 (0) to December 2030 (59): months counted as the views' Gantt counts them. */
export const GANTT_MONTHS = 60;
const clampMonth = (m: number) => Math.max(0, Math.min(GANTT_MONTHS - 1, Math.round(m)));

/** Where a milestone sits: its month, else the end of its year. */
export const milestoneMonth = (m: Milestone): number => clampMonth((m.y - CONSOLIDATION_YEARS[0]) * 12 + (m.m ?? 12) - 1);

/** A bar: first and last month. `from`: set by hand here, set in the views' Gantt, the span of its milestones, or the whole strategy. */
export interface Period {
  s: number;
  e: number;
  from: 'own' | 'views' | 'milestones' | 'whole';
}

/** A subproject's bar. Its milestones' span starts in January of the first milestone's year. */
export function subPeriod(sub: SubProject, p: ConsolidatedProject, state: StrategyState): Period {
  if (typeof sub.s === 'number' && typeof sub.e === 'number') return { s: clampMonth(sub.s), e: clampMonth(Math.max(sub.s, sub.e)), from: 'own' };
  const ed = sub.ref ? (state.edits[sub.ref] as Obj | undefined) : undefined;
  if (ed && typeof ed.gs === 'number' && typeof ed.ge === 'number') return { s: clampMonth(ed.gs), e: clampMonth(Math.max(ed.gs, ed.ge)), from: 'views' };
  const ms = p.milestones.filter((m) => m.sub === sub.id);
  if (ms.length) {
    const first = Math.min(...ms.map((m) => m.y));
    return { s: clampMonth((first - CONSOLIDATION_YEARS[0]) * 12), e: Math.max(...ms.map(milestoneMonth)), from: 'milestones' };
  }
  return { s: 0, e: GANTT_MONTHS - 1, from: 'whole' };
}

/** A project's bar: from its first subproject's start to its last one's end (and its own milestones). */
export function projectPeriod(p: ConsolidatedProject, state: StrategyState): { s: number; e: number } {
  const bars = p.subs.map((x) => subPeriod(x, p, state));
  const own = p.milestones.filter((m) => !m.sub).map(milestoneMonth);
  if (!bars.length && !own.length) return { s: 0, e: GANTT_MONTHS - 1 };
  return { s: Math.min(...bars.map((b) => b.s), ...own), e: Math.max(...bars.map((b) => b.e), ...own) };
}

/**
 * Which subproject needs which, from the links ("a depends on b": b first). A project split in two (two
 * subprojects with one ref) takes the link on its last part: the part that finishes the work.
 */
export function subDependencies(c: Consolidation, links: PlanLink[]): { from: string; to: string }[] {
  const last = new Map<string, string>();
  for (const p of c.projects) for (const x of p.subs) if (x.ref) last.set(x.ref, x.id);
  const seen = new Set<string>();
  const out: { from: string; to: string }[] = [];
  for (const l of links) {
    const ab = before(l);
    const from = ab && last.get(ab[0]);
    const to = ab && last.get(ab[1]);
    if (!from || !to || from === to || seen.has(`${from}>${to}`)) continue;
    seen.add(`${from}>${to}`);
    out.push({ from, to });
  }
  return out;
}

/** A dependency drawn red: the subproject starts before the one it needs has started. */
export const startsTooEarly = (needed: Period, needing: Period): boolean => needing.s < needed.s;

/**
 * A subproject's own text when it has one in either language, else its element's: never one language of each (a
 * title typed in Lithuanian shows in English as that, marked, not as the element's English title).
 */
export const ownText = (en?: string, lt?: string, elEn?: string, elLt?: string): { en: string; lt: string } =>
  en || lt ? { en: en ?? '', lt: lt ?? '' } : { en: elEn ?? '', lt: elLt ?? '' };

/** A text in the language shown: the other one when it has none. */
export const inLang = (lang: ConsolidationLang, en?: string, lt?: string): string => (lang === 'lt' ? lt || en || '' : en || lt || '');

/** The text shown has no version in the language shown: it is the other language's. */
export const fromOther = (lang: ConsolidationLang, en?: string, lt?: string): boolean => (lang === 'lt' ? !lt && !!en : !en && !!lt);
